import type { ComponentProps, ReactNode } from 'react';

import { cn } from '../cn';
import type { Shape, Size } from './types';
import { sizeIconClass, sizeIconPaddingL, sizeIconPaddingR } from './types';
import { badgeClassName, buttonClassName, composeVariant, softIconColorClasses } from './buttonClasses';
import type { ColorName, ColorVariant } from './buttonClasses';
import type { Style } from './colors';
import { useTooltipAnchor, TooltipContent } from './Tooltip';

export type ButtonAProps = Omit<ComponentProps<'a'>, 'size'> & {
  size?: Size;
  bordered?: boolean;
  shape?: Shape;
  icon?: ReactNode;
  rIcon?: ReactNode;
  error?: string;
  color?: ColorName;
  tone?: Style;
  active?: boolean;
  tooltip?: string;
  text?: boolean;
  disabled?: boolean;
  badge?: number | string;
};

export default function ButtonA({
  size = 'md',
  bordered = true,
  shape,
  icon,
  rIcon,
  error,
  color,
  tone,
  active = false,
  text,
  className,
  disabled,
  children,
  tooltip,
  badge,
  href,
  onMouseEnter,
  onMouseLeave,
  onFocus,
  onBlur,
  ...props
}: ButtonAProps) {
  const effectiveVariant: ColorVariant = composeVariant(color ?? 'gray', tone ?? 'filled');
  const { anchorName, anchorStyle, popoverRef, hoverHandlers } = useTooltipAnchor<HTMLAnchorElement>(
    tooltip,
    { onMouseEnter, onMouseLeave, onFocus, onBlur },
  );
  const iconOnly = !!icon && !children && !rIcon;
  const hasBadge = badge != null && badge !== 0 && badge !== '';
  return (
    <a
      href={href}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : undefined}
      style={anchorStyle}
      {...hoverHandlers}
      className={buttonClassName({
        size,
        bordered,
        shape,
        icon: !!icon,
        rIcon: !!rIcon,
        children: !!children,
        error: !!error,
        variant: effectiveVariant,
        active,
        text,
        disabled,
        tooltip: !!tooltip,
        className: cn(hasBadge && 'relative', disabled && 'pointer-events-none', className),
      })}
      {...props}>
      {hasBadge && (
        <span className={badgeClassName(effectiveVariant)}>
          {typeof badge === 'number' && badge > 99 ? '99+' : badge}
        </span>
      )}
      {icon && (
        <span className={cn('shrink-0 flex items-center justify-center', sizeIconPaddingL(size), iconOnly && sizeIconPaddingR(size), softIconColorClasses(effectiveVariant, active))}>
          <span className={cn(sizeIconClass(size), '*:size-full')}>{icon}</span>
        </span>
      )}
      {children && (
        <span className="whitespace-nowrap overflow-hidden text-ellipsis min-w-0">
          {children}
        </span>
      )}
      {rIcon && (
        <span className={cn('shrink-0 flex items-center justify-center', sizeIconPaddingR(size), softIconColorClasses(effectiveVariant, active))}>
          <span className={cn(sizeIconClass(size), '*:size-full')}>{rIcon}</span>
        </span>
      )}
      <TooltipContent anchorName={anchorName} variant={effectiveVariant} popoverRef={popoverRef}>
        {tooltip}
      </TooltipContent>
    </a>
  );
}
